import {
    Alert,
    Avatar,
    Button,
    ClickAwayListener,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tooltip,
    TooltipProps,
    Zoom,
    styled,
    tooltipClasses,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { enqueueSnackbar } from "notistack";
import { ReactElement, createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import {
    TbAlertTriangle,
    TbCar,
    TbEdit,
    TbPlus,
    TbProgress,
    TbProgressCheck,
    TbTrash,
    TbUserPin,
} from "react-icons/tb";

import { softColors } from "@/constants/colors";
import { getDateDiffInHours } from "@/helpers/date";
import { splitPlate } from "@/helpers/strings";
import { useCompleteShiftMutation } from "@/requests/daily-status.queries";
import { DriverStatus } from "@/types/daily-status.types";

import { useDriverStatusContext } from "../DriverStatusContext";
import { DriverStatusModals } from "../DriverStatusTable/components/DriverStatusColumn";
import DriverStatusColumnTimeTooltip from "./DriverStatusColumnTimeTooltip";

export const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: "none",
        backgroundColor: "white",
        border: "none",
        color: grey[800],
    },
});

function DriverStatusColumnTooltip({
    child,
    day,
    handleCreate,
    statuses,
    handleUpdate,
    handleDelete,
    activeModal,
    handleActiveModal,
}: {
    day: number;
    child: ReactElement;
    statuses: DriverStatus[];
    handleCreate: () => void;
    handleUpdate: (item: DriverStatus) => void;
    handleDelete: (id: string) => void;
    activeModal: DriverStatusModals | null;
    handleActiveModal: (modal?: DriverStatusModals) => void;
}) {
    const { t, i18n } = useTranslation();
    const { month, pagePermissions } = useDriverStatusContext();

    const handleTooltipClose = () => {
        if (activeModal !== DriverStatusModals.TOOLTIP) return;
        handleActiveModal();
    };

    const handleTooltipOpen = () => {
        handleActiveModal(DriverStatusModals.TOOLTIP);
    };
    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <div className="flex items-center w-full h-full ">
                <NoMaxWidthTooltip
                    onClose={handleTooltipClose}
                    open={activeModal === DriverStatusModals.TOOLTIP}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={
                        <div
                            className=" text-base py-2 flex flex-col  min-w-80 gap-1 "
                            onDoubleClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <div className="flex justify-between gap-3  whitespace-nowrap items-center">
                                <div className="flex items-center gap-1 ">
                                    <TbProgress />{" "}
                                    {month.set({ day }).setLocale(i18n.language).toFormat("dd MMMM yyyy")}{" "}
                                    {t("driverStatus")}
                                </div>
                            </div>
                            <Divider className="!bg-gray-200   !w-[80%] !mx-auto" />
                            {statuses.length ? (
                                <List dense sx={{ width: 450, padding: "0px !important" }}>
                                    {statuses.map((item) => (
                                        <StatusListItem
                                            handleDelete={handleDelete}
                                            handleUpdate={handleUpdate}
                                            item={item}
                                            key={item.id}
                                        />
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="warning">{t("driverNoStatus")}</Alert>
                            )}
                            <Divider className="!bg-gray-200  !w-[80%] !mx-auto" />

                            {pagePermissions.edit && (
                                <Button
                                    onClick={handleCreate}
                                    startIcon={<TbPlus></TbPlus>}
                                    type="submit"
                                    variant="outlined"
                                    color="primary"
                                >
                                    {t("buttons.createNewStatus")}
                                </Button>
                            )}
                        </div>
                    }
                    slots={{
                        transition: Zoom,
                    }}
                    arrow
                >
                    <div onClick={handleTooltipOpen} className="flex items-center w-full h-full ">
                        {child}
                    </div>
                </NoMaxWidthTooltip>
            </div>
        </ClickAwayListener>
    );
}
export function StatusListItem({
    item,
    handleUpdate,
    handleDelete,
}: {
    handleUpdate: (item: DriverStatus) => void;
    handleDelete: (id: string) => void;

    item: DriverStatus;
}) {
    const { t } = useTranslation();
    const { vehicles, pagePermissions } = useDriverStatusContext();
    const plate = useMemo(() => vehicles.find((cell) => cell.id === item.vehicleId)?.plate, [vehicles, item]);

    const { mutateAsync, isPending } = useCompleteShiftMutation();

    const completeDriverStatus = async () => {
        if (!item.userId || isPending) return;
        try {
            await mutateAsync({ userId: item.userId });
            enqueueSnackbar(t("driverStatusUpdated"), { variant: "success" });
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <ListItem
                key={item.id}
                secondaryAction={
                    (pagePermissions.edit && (
                        <>
                            <Tooltip title={t("buttons.delete")}>
                                <IconButton
                                    onClick={() => {
                                        handleDelete(item.id);
                                    }}
                                    edge="end"
                                    color="error"
                                    size="small"
                                    aria-label="delete"
                                >
                                    <TbTrash />
                                </IconButton>
                            </Tooltip>
                            {item.isActive && (
                                <Tooltip title={t("buttons.completeShift")}>
                                    <IconButton
                                        onClick={completeDriverStatus}
                                        edge="end"
                                        color="primary"
                                        size="small"
                                        aria-label="delete"
                                    >
                                        <TbProgressCheck />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={<span>{t("buttons.update")}</span>}>
                                <IconButton
                                    onClick={() => {
                                        handleUpdate(item);
                                    }}
                                    edge="end"
                                    color="primary"
                                    size="small"
                                    aria-label="delete"
                                >
                                    <TbEdit />
                                </IconButton>
                            </Tooltip>
                        </>
                    )) ||
                    false
                }
            >
                <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: item.component?.hover }}>
                        {createElement(item.component?.component() || TbProgress, {
                            fontSize: 20,
                        })}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <div className="flex flex-col pr-11 ">
                            <div className="flex text-xs  font-medium  items-center gap-1 justify-center">
                                <span>
                                    <DriverStatusColumnTimeTooltip
                                        title="startLocationInfo"
                                        item={item}
                                        time={item.startTime}
                                        list={addressItems.start}
                                    />
                                    -{" "}
                                    <DriverStatusColumnTimeTooltip
                                        title="endLocationInfo"
                                        time={item.endTime}
                                        list={addressItems.end}
                                        item={item}
                                    />
                                </span>
                                {item.startTime &&
                                    item.endTime &&
                                    `(${getDateDiffInHours(item.startTime, item.endTime || Date.now().toString())})`}

                                {item.isAutoEnded && (
                                    <Tooltip
                                        title="Mesai süresi 13 saati geçtiğinden dolayı otomatik olarak bitirildi"
                                        placement="top"
                                    >
                                        <span
                                            className="inline-flex absolute top-3 right-8"
                                            tabIndex={0}
                                            aria-label="Otomatik bitirilen mesai bilgisi"
                                        >
                                            <TbAlertTriangle className="text-orange-600" size={14} />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            <div className=" flex justify-between items-center font-medium gap-1">
                                <div>{t("driverStatuses." + item.status)}</div>

                                {plate && (
                                    <span className="  text-xs   flex items-center gap-1 ">{splitPlate(plate)}</span>
                                )}
                            </div>
                        </div>
                    }
                    secondary={<div className=" pr-2">{t(`driverStatuses.${item.status}_DESC`)}</div>}
                />
            </ListItem>
        </>
    );
}

export type AddressItem = {
    key: keyof DriverStatus;
    backgroundColor: string;
    coordinates: keyof DriverStatus;
    title: string;
    icon: IconType;
};
export const addressItems: {
    start: AddressItem[];
    end: AddressItem[];
} = {
    start: [
        {
            title: "carLocation",
            key: "vehiclestartAddress",
            coordinates: "vehiclestartLocation",
            backgroundColor: softColors.blue.hover,
            icon: TbCar,
        },
        {
            title: "driverLocation",
            key: "driverStartAddress",
            coordinates: "driverStartLocation",
            backgroundColor: softColors.green.hover,
            icon: TbUserPin,
        },
    ],
    end: [
        {
            title: "carLocation",
            key: "vehicleEndAddress",
            coordinates: "vehicleEndLocation",
            backgroundColor: softColors.blue.hover,
            icon: TbCar,
        },
        {
            title: "driverLocation",
            key: "driverEndAddress",
            coordinates: "driverEndLocation",
            backgroundColor: softColors.green.hover,
            icon: TbUserPin,
        },
    ],
};

export default DriverStatusColumnTooltip;
